import React, { useState, useRef, useEffect } from 'react';
import { X, Brain, Lightbulb } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    getResolveSuggestedPrompts,
    RESOLVE_CATEGORIES,
    type ResolveSuggestedPrompt,
    type ResolveCategoryType
} from '../constants/resolveSuggestedPrompts';

// Refactored Components & Constants
import {
    Message,
    MAGNUS_AVATAR,
    MAGNUS_EMOJI_VARIANTS,
    PAGE_CONTEXT
} from '../constants/MagnusResolveAssistantConstants';
import { SuggestedPromptsPanel } from './assistant/SuggestedPromptsPanel';
import { ChatPanel } from './assistant/ChatPanel';
import { handleBasicQuery } from '../utils/assistant/assistantLogic';
import { useAssistantContext } from '../hooks/useAssistantContext';

export const MagnusResolveAssistant: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    useAuth();
    const { getActiveContextSummary } = useAssistantContext();
    const currentPageContext = PAGE_CONTEXT[location.pathname];

    const [isOpen, setIsOpen] = useState(false);
    const [showIntroBubble, setShowIntroBubble] = useState(true);
    const [isPrimeMode, setIsPrimeMode] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [showSuggestedPrompts, setShowSuggestedPrompts] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<ResolveCategoryType | null>(null);
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'magnus',
            content: currentPageContext
                ? `${MAGNUS_EMOJI_VARIANTS.resolve} Hey! You're on **${currentPageContext.name}**. ${currentPageContext.purpose}\n\nI can help you **navigate** (Basic Mode) or **analyze data** (Prime Mode).`
                : `${MAGNUS_AVATAR} Hello! I'm Magnus, your reliability engineering assistant. How can I help?`
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const allPrompts = getResolveSuggestedPrompts();
    const filteredPrompts = selectedCategory
        ? allPrompts.filter(p => p.category === selectedCategory)
        : allPrompts;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        const ctx = PAGE_CONTEXT[location.pathname];
        const greeting = ctx ? `You're now on **${ctx.name}**. ${ctx.purpose}` : "How can I help you?";
        setMessages([{ role: 'magnus', content: greeting }]);
    }, [location.pathname]);

    const handleClear = () => {
        const ctx = PAGE_CONTEXT[location.pathname];
        setMessages([{
            role: 'magnus',
            content: ctx ? `Hey! You're on **${ctx.name}**. What would you like to do?` : "Hello! How can I help you?"
        }]);
    };

    const handleSend = async () => {
        if (!inputValue.trim()) return;
        const userMessage = inputValue.trim();
        setInputValue('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

        if (isPrimeMode) {
            setIsThinking(true);
            const ctx = getActiveContextSummary();
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    role: 'magnus',
                    content: `**[Prime Mode]** Analyzing with **Active Data Link**...\n\n` +
                        `📊 **Live Context (${ctx.type})**:\n${ctx.summary}\n${ctx.details}\n\n` +
                        `Your question: "${userMessage}"\n\n` +
                        `*I have injected this specific page data into the AI analysis context.*`
                }]);
                setIsThinking(false);
            }, 1500);
        } else {
            const response = handleBasicQuery(userMessage, currentPageContext);
            setMessages(prev => [...prev, { role: 'magnus', content: response.answer, action: response.action }]);
        }
    };

    const handlePromptClick = (prompt: ResolveSuggestedPrompt) => {
        setInputValue(prompt.prompt);
        setShowSuggestedPrompts(false);
        if (isPrimeMode) {
            setInputValue('');
            setMessages(prev => [...prev, { role: 'user', content: prompt.prompt }]);
            const ctx = getActiveContextSummary();
            setIsThinking(true);
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    role: 'magnus',
                    content: `**[Prime Mode - ${prompt.category}]** ${MAGNUS_EMOJI_VARIANTS.analyzing}\n\n` +
                        `Analyzing **${prompt.title}** with **Active Data Link**...\n\n` +
                        `📊 **Live Context (${ctx.type})**:\n${ctx.summary}\n${ctx.details}\n\n` +
                        `*Generating insights based on this data...*`
                }]);
                setIsThinking(false);
            }, 1500);
        }
    };

    const handleAction = (action: any) => {
        if (action?.type === 'navigate') {
            navigate(action.target);
            setIsOpen(false);
        }
    };

    return (
        <>
            {!isOpen && !showSuggestedPrompts && (
                <button
                    onClick={() => setShowSuggestedPrompts(true)}
                    className="fixed bottom-24 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-xl hover:shadow-yellow-500/50 transition-all duration-300 hover:scale-110 z-40 flex items-center justify-center"
                    title="Suggested Prompts"
                >
                    <Lightbulb className="w-6 h-6" />
                </button>
            )}

            <SuggestedPromptsPanel
                showSuggestedPrompts={showSuggestedPrompts}
                setShowSuggestedPrompts={setShowSuggestedPrompts}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                allPrompts={allPrompts}
                filteredPrompts={filteredPrompts}
                categories={RESOLVE_CATEGORIES}
                handlePromptClick={handlePromptClick}
            />

            {!isOpen && showIntroBubble && (
                <div className="fixed bottom-24 right-6 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-xl border-l-4 border-purple-500 max-w-xs z-40 animate-fade-in">
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-purple-600 dark:text-purple-400">Hi, I'm Magnus! 👋</h4>
                        <button onClick={() => setShowIntroBubble(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        I have two modes:
                        <br /><span className="font-semibold text-blue-500">⚡ Basic:</span> I'll help you navigate.
                        <br /><span className="font-semibold text-yellow-500">🚀 Prime:</span> I'll analyze your data deeply.
                    </p>
                </div>
            )}

            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center group"
                title="Magnus AI Assistant"
                data-tour-id="magnus-assistant"
            >
                <img
                    src="/magnus-icon.png"
                    alt="Magnus"
                    className="w-10 h-10 group-hover:scale-110 transition-transform"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                />
                <Brain className="hidden w-8 h-8" />
                {!isOpen && <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse"></span>}
            </button>

            <ChatPanel
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                isPrimeMode={isPrimeMode}
                setIsPrimeMode={setIsPrimeMode}
                handleClear={handleClear}
                messages={messages}
                isThinking={isThinking}
                inputValue={inputValue}
                setInputValue={setInputValue}
                handleSend={handleSend}
                handleAction={handleAction}
                messagesEndRef={messagesEndRef}
            />
        </>
    );
};

export default MagnusResolveAssistant;
